"use client";

import React, { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { X, Download, Loader2 } from "lucide-react";
import api from "../services";

interface AppleWalletQRDialogProps {
  userId: string | undefined;
  isOpen: boolean;
  onClose: () => void;
}

const AppleWalletQRDialog: React.FC<AppleWalletQRDialogProps> = ({
  userId,
  isOpen,
  onClose,
}) => {
  const [qrCodeUrl, setQrCodeUrl] = useState<string>("");
  const [passUrl, setPassUrl] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    console.log("Fetching pass data for userId:", userId, isOpen);
    if (isOpen && userId) {
      console.log("Fetching pass data for userId:", userId, isOpen);
      fetchPassData();
    }
  }, [isOpen, userId]);

  const fetchPassData = async () => {
    try {
      setLoading(true);
      setError("");

      // Change from api.post to api.get
      const res = await api.get(`/wallet/qr/${userId}`);

      const url = res.data.passUrl;
      setPassUrl(url);

      // Generate QR code
      const qrDataUrl = await QRCode.toDataURL(url, {
        width: 300,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
      });

      setQrCodeUrl(qrDataUrl);
    } catch (err: any) {
      console.error("Error generating QR code:", err);
      setError(err?.response?.data?.message || "Failed to generate QR code");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPass = () => {
    if (passUrl) {
      window.location.href = passUrl;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin bg-gray-900 text-white dm-wallet-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-center">
            Add to Apple Wallet
          </DialogTitle>
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        </DialogHeader>

        <div className="space-y-6 pt-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
              <p className="mt-4 text-white/60">Generating your pass...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="rounded-full bg-red-500/10 p-3">
                <X className="h-8 w-8 text-white" />
              </div>
              <p className="mt-4 text-white/80">{error}</p>
              <button
                onClick={fetchPassData}
                className="mt-4 px-4 py-2 bg-main/90 text-white rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : (
            <>
              {/* Instructions */}
              <div className="text-center space-y-2">
                <h3 className="text-lg font-medium text-white">
                  This pass needs to be added to the Wallet app on your iPhone
                </h3>
                <p className="text-sm text-white/60">
                  Please ensure you use your mobile camera (no third-party apps)
                  to scan the QR code
                </p>
              </div>

              {/* QR Code */}
              {qrCodeUrl && (
                <div className="flex justify-center">
                  <div className="bg-white p-8 rounded-xl shadow-lg inline-block">
                    <img
                      src={qrCodeUrl}
                      alt="Scan to add to Apple Wallet"
                      className="w-72 h-72"
                    />
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="text-center text-xs text-white/60 pt-4 border-t border-white/10">
                Powered by PassKit™
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AppleWalletQRDialog;
